import { Injectable, EventEmitter } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class KeyboardService {

  private inputValueSubject = new BehaviorSubject<string>('');
  inputValue$ = this.inputValueSubject.asObservable();

  private lastKeySubject = new BehaviorSubject<string | null>(null);
  lastKey$ = this.lastKeySubject.asObservable();

  private _isSystemTamilKeyboard = new BehaviorSubject<boolean>(false);
  isSystemTamilKeyboard$ = this._isSystemTamilKeyboard.asObservable();

  isMei(char: string | null): boolean {
    if (!char) return false;
    return ['க்', 'ச்', 'ட்', 'த்', 'ப்', 'ற்', 'ங்', 'ஞ்', 'ண்', 'ந்', 'ம்', 'ன்', 'ய்', 'ர்', 'ல்', 'வ்', 'ழ்', 'ள்'].includes(char);
  }

  enterKeyEvent = new EventEmitter<void>();

  constructor() { }

  getUyirMeiCombination(char: string): string | null {
    const currentInput = this.inputValueSubject.value;
    const lastChar = currentInput.slice(-2);

    const uyirToSymbolMap: { [key: string]: string } = {
      'அ': '', 'ஆ': 'ா', 'இ': 'ி', 'ஈ': 'ீ', 'உ': 'ு', 'ஊ': 'ூ', 
      'எ': 'ெ', 'ஏ': 'ே', 'ஐ': 'ை', 'ஒ': 'ொ', 'ஓ': 'ோ', 'ஔ': 'ௌ'
    };

    if (this.isMei(lastChar)) {
      const symbol = uyirToSymbolMap[char];
      if (symbol !== undefined) {
        const baseConsonant = lastChar[0];
        return baseConsonant + symbol;
      }
    }

    return null;
  }

  playAudioForChar(char: string) {
    let audio = new Audio(`assets/audio/${char}.mp3`);
    audio.play().catch(error => console.error('Error playing audio:', error));
  }

  onChar(value: string) {
    this.lastKeySubject.next(value);
    this.inputValueSubject.next(this.processChar(value));
  }

  processChar(value: string): string {
    const currentInput = this.inputValueSubject.value;

    // Check if value is a vowel sign
    if (['', 'ா', 'ி', 'ீ', 'ு', 'ூ', 'ெ', 'ே', 'ை', 'ொ', 'ோ', 'ௌ'].includes(value)) {
      // If the last character of the current input is a pulli
      if (currentInput.endsWith('்')) {
        // Play audio for the combination
        const combination = currentInput.slice(-2, -1) + value;
        this.playAudioForChar(combination);
        return currentInput.slice(0, -1) + value;
      }
    }

    // If user presses space and the last character is not a vowel, vowel sign, or pulli
    if (value === ' ' && !'அஆஇஈஉஊஎஏஐஒஓஔாிீுூெேைொோௌ்'.includes(currentInput.slice(-1))) {
      // If the last character is a Mei consonant, add pulli to the last consonant and add space
      const lastChar = currentInput.slice(-1);
      if (this.isMei(lastChar) && !lastChar.endsWith('்')) {
        return currentInput + '்' + value;
      }
    }

    // Play audio for non-space characters
    if (value !== ' ') {
      this.playAudioForChar(value);
    }

    return currentInput + value;
  }

  onDelete() {
    let currentValue = this.inputValueSubject.value;
    const uyirSigns = ['ா', 'ி', 'ீ', 'ு', 'ூ', 'ெ', 'ே', 'ை', 'ொ', 'ோ', 'ௌ'];

    if (uyirSigns.includes(currentValue.slice(-1))) {
      this.inputValueSubject.next(currentValue.slice(0, -2));
    } else if (currentValue.slice(-1) === '்') {
      this.inputValueSubject.next(currentValue.slice(0, -2));
    } else {
      this.inputValueSubject.next(currentValue.slice(0, -1));
    }
  }

  onEnter() {
    let currentValue = this.inputValueSubject.value;
    this.inputValueSubject.next(currentValue + '\n');
    this.enterKeyEvent.emit();
  }

  onSpace() {
    let currentValue = this.inputValueSubject.value;
    this.inputValueSubject.next(currentValue + ' ');
    this.lastKeySubject.next(null);
  }

  resetInputValue() {
    this.inputValueSubject.next('');
  }

  clearLastKey() {
    this.lastKeySubject.next(null);
  }

  setKeyboardType(isSystemTamil: boolean) {
    const isTouchDevice = navigator.maxTouchPoints ? navigator.maxTouchPoints > 2 : false;
    const isMobileOrTablet = /iPhone|iPad|iPod|Android|tablet|Tablet/i.test(navigator.userAgent) || 
                            isTouchDevice;
    
    if (isMobileOrTablet && this.isTamilInputEnabled()) {
      this._isSystemTamilKeyboard.next(true);
    } else {
      this._isSystemTamilKeyboard.next(isSystemTamil);
    }
  }

  private isTamilInputEnabled(): boolean {
    return navigator.language.includes('ta') || 
           document.documentElement.lang.includes('ta') ||
           (navigator as any).userLanguage === 'ta' ||
           /Tamil|தமிழ்/i.test((navigator as any).keyboard?.layouts?.[0]?.name || '') ||
           ('InputMethodManager' in window || 
            'webkitInputMethodManager' in window);
  }

  getInputValue(): string {
    return this.inputValueSubject.getValue();
  }

  setInputValue(value: string) {
    this.inputValueSubject.next(value);
  }
}